'use client';

import { usePrivy, useWallets, useLogin, useLogout, useLinkAccount, useFundWallet } from '@privy-io/react-auth';
import { useSetActiveWallet } from '@privy-io/wagmi';
import { useAccount, useDisconnect, useSwitchChain } from 'wagmi';
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getHighResAvatarUrl } from '@/lib/utils';
import { clientChain } from '@/lib/viem-client';
import { useTutorialWalletOptional } from '@/features/tutorial/wallet-shim';
import {
  removeAddressScopedQueries,
  resetAddressKeyedQueries,
  resetQueriesByPrefixes,
} from '@/lib/query-cache-cleanup';
import {
  attemptLinkActiveWallet,
  attemptSignInActiveWallet,
  type LinkableWallet,
  type LinkActiveWalletResult,
} from './wallet/link-active-wallet';
import {
  fetchTakeoverBalance,
  fetchUsdcBalance,
} from './wallet/balance-fetchers';

// Re-exports preserve existing import paths (test files,
// `features/tutorial/wallet-shim.tsx`, etc.) while the canonical
// homes are under `hooks/wallet/`. Future consumers should import
// from there directly.
export { attemptLinkActiveWallet, attemptSignInActiveWallet };
export type { LinkableWallet, LinkActiveWalletResult };

/**
 * TanStack query-key prefixes whose data is scoped to the viewer's
 * wallet address. Wallet-switch wipes these via
 * `resetQueriesByPrefixes` so the HUD falls back to its skeleton
 * rather than painting the previous wallet's values while the new
 * SIWE resolves.
 *
 * Some entries are already covered by the address-segment predicate
 * in `resetAddressKeyedQueries` (`chat-verify`, `db-notifications`,
 * `user-profile`, etc.) — we list them anyway so a future refactor
 * that drops the address from the key still resets on switch.
 * Address-independent caches (chat-messages, takeover-price,
 * eth-price, lobby grid feed, board metadata) intentionally aren't
 * here and aren't touched.
 */
const ADDRESS_DEPENDENT_QUERY_PREFIXES: readonly string[] = [
  'wallet',
  'portfolio',
  'pending-fees',
  'claimable-fees',
  'creator-fees',
  'creator-fees-batch',
  'user-profile',
  'usdc-balance',
  'usdc-balance-cached',
  'takeover-balance',
  'chat-verify',
  'db-notifications',
  'notification-preferences',
  // `joined-boards` shares `['wallet', 'portfolio', addr]`, already
  // covered by 'wallet' + 'portfolio' above. Not re-listed to avoid
  // the temptation of adding a parallel key by mistake.
];

/**
 * Debounce window (ms) for the wallet-switch cache cascade. Long
 * enough that Privy's internal A→null→B transient never fires the
 * cascade twice, short enough that the user sees fresh wallet data
 * within one frame of the real switch landing. 200ms was measured
 * as the 95th-percentile gap between the `null` flicker and the
 * new address surfacing on staging.
 */
const WALLET_SWITCH_DEBOUNCE_MS = 200;

const chain = clientChain;
const expectedChainId = chain.id;

export function useWallet() {
  // Tutorial mode: read the optional shim up-front (so the hook count is
  // stable) and override at the end. The real Privy/wagmi hooks still run
  // because Privy is mounted globally; we just hide their values from
  // tutorial consumers so nothing they render reflects the live wallet.
  const tutorialWallet = useTutorialWalletOptional();
  const { ready, authenticated, user, getAccessToken, exportWallet } = usePrivy();
  // `walletsReady` is the documented guard for the "just connected via
  // wagmi but not yet surfaced in `useWallets()`" race window. Per the
  // Privy docs (Get connected wallets → Waiting for wallets to be
  // ready), the array is fluid until this flag flips true. Anything
  // that calls `wallet.loginOrLink()` MUST gate on it or the lookup
  // races the wagmi connect callback.
  const { wallets, ready: walletsReady } = useWallets();
  const { setActiveWallet } = useSetActiveWallet();
  const { logout: privyLogout } = useLogout();
  const { mutate: wagmiDisconnect } = useDisconnect();
  const { mutateAsync: switchChainAsync } = useSwitchChain();
  const queryClient = useQueryClient();
  const [isLinkingAccount, setIsLinkingAccount] = useState(false);
  const { fundWallet: privyFundWallet } = useFundWallet();

  // Per-session dedupe set for `/api/verify-social` POSTs. See the
  // `recordVerifications` effect below for the full rationale —
  // declared here at the top of the hook so both the
  // `useLinkAccount` callback (which writes on success) and the
  // post-mount effect (which reads + writes) share the same ref.
  const recordedSocialsRef = useRef<Set<string>>(new Set());

  // Pull a fresh copy of `/api/users/[address]` into the React Query
  // cache after a social link lands in the DB. A plain
  // `invalidateQueries` is not enough here: the route is served with
  // `public, max-age=60-300` so the "refetch" is answered by the
  // browser/CDN HTTP cache with the same pre-link payload. The `fresh`
  // query param changes the cache key and punches through; on any
  // failure we fall back to a normal invalidation so the UI still
  // converges once the HTTP cache expires.
  const refreshUserProfileCache = useCallback(
    async (addr: string) => {
      const lower = addr.toLowerCase();
      try {
        const res = await fetch(
          `/api/users/${encodeURIComponent(lower)}?fresh=${Date.now()}`,
        );
        if (res.ok) {
          const json = await res.json();
          if (json?.data) {
            queryClient.setQueryData(['user-profile', lower], json.data);
            return;
          }
        }
      } catch {
        // fall through to invalidation
      }
      await queryClient.invalidateQueries({
        queryKey: ['user-profile', lower],
        refetchType: 'all',
      });
    },
    [queryClient],
  );

  // Wagmi provides a stable, reconnect-aware address that survives
  // mobile deep-link round-trips (MetaMask → browser).
  const wagmiAccount = useAccount();
  const wagmiAddress = wagmiAccount.address;
  const wagmiChainId = wagmiAccount.chainId;
  const wagmiConnected = wagmiAccount.isConnected;

  // Bridge MetaMask (and any EIP-1193) account-switch events into
  // wagmi.
  //
  // Privy's wagmi adapter (`@privy-io/wagmi`) only flips the
  // wagmi-active wallet via explicit `setActiveWallet()` calls.
  // When the user switches accounts inside MetaMask's own UI, the
  // injected provider emits `accountsChanged`, but neither Privy's
  // useWallets() nor wagmi's useAccount() pick that up on their own
  // — wagmi's address stays pinned to whatever Privy was last told
  // is active. That's the symptom the chat panel sees as "wallet
  // switched but chat keeps showing the old session": chat-verify
  // is keyed on `address`, the key never changes, and no refetch
  // ever runs.
  //
  // Two-effect bridge:
  //
  //   1. Provider listener — attached to the wagmi-active wallet's
  //      underlying EIP-1193 provider. When `accountsChanged` fires
  //      with an address that differs from the current wagmi-active
  //      one, we record it as a "pending switch" in state.
  //
  //   2. Pending-switch reconciler — when there's a pending address,
  //      look it up in `useWallets()` and call `setActiveWallet()`.
  //      If Privy hasn't surfaced the new entry yet, the effect
  //      no-ops; the next `wallets` update re-runs it and we try
  //      again. This is the race we have to tolerate: Privy's
  //      connector may add the new account to its list in the same
  //      tick that the provider event fires, but we can't rely on
  //      ordering.
  //
  // Once `setActiveWallet()` resolves, wagmi emits its own
  // `change` and our `useAccount()` flips to the new address —
  // every downstream query keyed on `address` (chat-verify,
  // notifications, push subscribe) refetches without any extra
  // glue.
  const [pendingActiveAddress, setPendingActiveAddress] = useState<string | null>(null);
  // Last `pinned → live` transition the proactive reconciler attempted,
  // so a transition that can't take effect isn't retried in a loop.
  const reconcileKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!walletsReady || !wagmiAddress) return;
    const activeEntry = wallets.find(
      (w) => w.address.toLowerCase() === wagmiAddress.toLowerCase(),
    );
    if (!activeEntry?.getEthereumProvider) return;

    let cancelled = false;
    let detach: (() => void) | undefined;

    activeEntry
      .getEthereumProvider()
      .then((provider) => {
        if (cancelled) return;

        const handler = (rawAccounts: unknown) => {
          const list = Array.isArray(rawAccounts) ? (rawAccounts as string[]) : [];
          const newAddr = list[0]?.toLowerCase();
          if (!newAddr || newAddr === wagmiAddress.toLowerCase()) return;
          setPendingActiveAddress(newAddr);
        };

        // EIP-1193 providers expose `on` / `removeListener`. Type
        // them defensively — Privy's `getEthereumProvider()` returns
        // a more permissive shape than the wagmi connector consumes.
        const eip1193 = provider as unknown as {
          on?: (event: string, listener: (...args: unknown[]) => void) => void;
          removeListener?: (event: string, listener: (...args: unknown[]) => void) => void;
        };
        if (typeof eip1193.on !== 'function') return;
        eip1193.on('accountsChanged', handler);
        detach = () => {
          if (typeof eip1193.removeListener === 'function') {
            eip1193.removeListener('accountsChanged', handler);
          }
        };
      })
      .catch(() => {
        // Provider read failed — connector probably hasn't fully
        // initialised. The effect re-runs on the next wallets/
        // wagmiAddress change, which is the natural retry point.
      });

    return () => {
      cancelled = true;
      detach?.();
    };
  }, [walletsReady, wagmiAddress, wallets]);

  useEffect(() => {
    if (!pendingActiveAddress) return;
    if (!walletsReady) return;
    // Already done — clear the pending marker and bail.
    if (wagmiAddress?.toLowerCase() === pendingActiveAddress) {
      setPendingActiveAddress(null);
      return;
    }
    const matching = wallets.find(
      (w) => w.address.toLowerCase() === pendingActiveAddress,
    );
    if (!matching) {
      // Privy hasn't surfaced this account yet. Re-run when
      // `wallets` updates; if Privy never surfaces it (e.g. the
      // user switched to an account they've never connected via
      // Privy), the pending marker stays set but doesn't break
      // anything — chat-verify keeps using the (still-valid) wagmi
      // address, and clicking Sign in via the user's own action
      // remains the recovery path.
      return;
    }
    let cancelled = false;
    // Stop any in-flight chat-verify for the OLD wagmi address from
    // competing for envio bandwidth during the switch window. The
    // chat-panel queryKey is now lowercased (`addressKey` in
    // `chat-panel.tsx`) so we lowercase here too — without
    // normalisation a wagmi-emitted checksummed address wouldn't
    // match the lowercased queryKey and the cancel would silently
    // skip the in-flight query, defeating the wallet-switch
    // bandwidth guard.
    if (wagmiAddress) {
      queryClient.cancelQueries({
        queryKey: ['chat-verify', wagmiAddress.toLowerCase()],
      });
    }
    setActiveWallet(matching)
      .then(() => {
        if (!cancelled) setPendingActiveAddress(null);
      })
      .catch((err) => {
        console.warn('[use-wallet] setActiveWallet failed', err);
        if (!cancelled) setPendingActiveAddress(null);
      });
    return () => {
      cancelled = true;
    };
  }, [pendingActiveAddress, walletsReady, wagmiAddress, wallets, setActiveWallet, queryClient]);

  // Proactive active-wallet reconcile.
  //
  // The `accountsChanged` listener above only catches a switch that
  // happens *while* it's attached. If the user switched accounts in
  // their wallet before the listener mounted — or Privy pinned the
  // wagmi-active wallet to a stale account on reconnect — wagmi keeps
  // signing (and reporting `holder`) as the OLD address. That's exactly
  // how the Deposit-Protection enable ends up rejected with "this
  // address is a smart account" against the *previous* account: the
  // wallet's live account is a clean EOA, but the app signs as the
  // pinned one. Here we read the wagmi-active wallet's *live* provider
  // account and, on a genuine mismatch, route it through the same
  // pending-switch reconciler. `eth_accounts` is a local read (no
  // prompt). No-op whenever the live account already matches (the normal
  // case), so no other flow's behaviour changes.
  useEffect(() => {
    if (!walletsReady || !wagmiAddress || pendingActiveAddress) return;
    const activeEntry = wallets.find(
      (w) => w.address.toLowerCase() === wagmiAddress.toLowerCase(),
    );
    if (!activeEntry?.getEthereumProvider) return;

    let cancelled = false;
    void activeEntry
      .getEthereumProvider()
      .then(async (provider) => {
        if (cancelled) return;
        const eip1193 = provider as unknown as {
          request?: (args: { method: string }) => Promise<unknown>;
        };
        if (typeof eip1193.request !== 'function') return;
        const res = await eip1193.request({ method: 'eth_accounts' });
        if (cancelled) return;
        const live = Array.isArray(res)
          ? (res[0] as string | undefined)?.toLowerCase()
          : undefined;
        if (!live || live === wagmiAddress.toLowerCase()) return;

        // Loop guard: only attempt a given `pinned → live` transition
        // once. If `setActiveWallet` succeeds, `wagmiAddress` becomes
        // `live` and the mismatch is gone; if it can't (Privy re-pins
        // the stale address), we don't retry the identical transition.
        const key = `${wagmiAddress.toLowerCase()}>${live}`;
        if (reconcileKeyRef.current === key) return;
        reconcileKeyRef.current = key;

        // Prefer a linked wallet entry for the live account; otherwise
        // re-activate the same connector so wagmi re-reads its current
        // account (covers the single-connector account-switch case where
        // Privy never surfaces the new account as its own entry).
        const target =
          wallets.find((w) => w.address.toLowerCase() === live) ?? activeEntry;
        void setActiveWallet(target).catch((err) => {
          console.warn('[use-wallet] active-wallet reconcile failed', err);
        });
      })
      .catch(() => {
        // Provider not ready / read failed — the next `wallets` or
        // `wagmiAddress` change re-runs this.
      });

    return () => {
      cancelled = true;
    };
  }, [walletsReady, wagmiAddress, wallets, pendingActiveAddress, setActiveWallet]);

  // Derive canonical address: prefer wagmi's connected address (handles
  // mobile reconnection correctly), fall back to Privy's linked accounts
  // for embedded-wallet-only users where wagmi may not have a connector.
  const address = useMemo(() => {
    if (wagmiAddress) return wagmiAddress;

    if (!user) return null;
    let embedded: string | null = null;
    for (const account of user.linkedAccounts as unknown as { type: string; walletClientType?: string; address?: string }[]) {
      if (
        (account.type === 'wallet' || account.type === 'smart_wallet') &&
        account.address
      ) {
        if (account.walletClientType === 'privy' || account.walletClientType === 'privy-v2') {
          embedded = embedded || account.address;
        }
      }
    }
    return embedded;
  }, [wagmiAddress, user]);

  // Set of every wallet-type address Privy knows the viewer owns, lowercased.
  // The backend `requireAuth` check rejects (403) any request where the
  // `?address=` param isn't in this set, so surfacing it here lets wallet-
  // scoped fetchers (`useNotifications`, push subscribe, etc.) skip the
  // round-trip when wagmi has surfaced an in-browser address that Privy
  // hasn't linked — e.g. the user switched MetaMask accounts mid-session.
  const linkedAddresses = useMemo<Set<string>>(() => {
    const s = new Set<string>();
    if (!user) return s;
    for (const account of user.linkedAccounts as unknown as { type: string; address?: string }[]) {
      if ((account.type === 'wallet' || account.type === 'smart_wallet') && account.address) {
        s.add(account.address.toLowerCase());
      }
    }
    return s;
  }, [user]);

  // True when the current `address` is a wallet Privy has linked to the
  // session. Before `user` hydrates we hold `authenticated` as the
  // optimistic answer — otherwise wagmi's address arrives ahead of Privy
  // and the first render flashes into the "unlinked" branch, killing
  // auth-gated queries unnecessarily on boot.
  const isAddressLinked = useMemo(() => {
    if (!address) return false;
    if (!user) return authenticated;
    return linkedAddresses.has(address.toLowerCase());
  }, [address, user, authenticated, linkedAddresses]);

  const { linkTwitter, linkFarcaster, linkWallet } = useLinkAccount({
    onSuccess: async ({ user: updatedUser, linkMethod, linkedAccount }) => {
      // Wallet-link branch: a SIWE link via `wallet.loginOrLink()` (or
      // the `linkWallet()` modal) lands here with `linkedAccount.type
      // === 'wallet'`. We don't gate on `linkMethod` because that
      // string isn't formally documented for SIWE — `linkedAccount.
      // type` is the stable contract per the Privy linked-accounts
      // docs. Invalidating `chat-verify` lets `chat-panel.tsx` refetch
      // against the now-known wallet without a manual reload.
      if (linkedAccount && (linkedAccount as { type?: string }).type === 'wallet') {
        const linkedAddr = (linkedAccount as { address?: string }).address;
        if (linkedAddr) {
          // Refetch the holder check for the freshly-linked wallet.
          // Scoped to `['chat-verify', <addr>, ...]` so we don't
          // disturb other rooms' caches; the `address` segment is
          // present in every chat-verify key (see chat-panel.tsx).
          queryClient.invalidateQueries({
            predicate: (query) =>
              Array.isArray(query.queryKey) &&
              query.queryKey[0] === 'chat-verify' &&
              typeof query.queryKey[1] === 'string' &&
              (query.queryKey[1] as string).toLowerCase() === linkedAddr.toLowerCase(),
          });
        }
        return;
      }

      if (linkMethod !== 'twitter' && linkMethod !== 'farcaster') return;

      if (!address) return;

      setIsLinkingAccount(true);
      try {
        const token = await getAccessToken();
        const authHeaders: Record<string, string> = {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };

        const verifyRes = await fetch('/api/verify-social', {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ platform: linkMethod }),
        });

        // Mark this (platform, username) as recorded for the session so
        // the `recordVerifications` effect (which also POSTs
        // `/api/verify-social`) won't re-fire when Privy emits the new
        // linked-account info on its next user tick. Only on a 2xx —
        // `fetch` resolves fine on 401/429/503, and recording a failed
        // attempt here would stop the effect from ever retrying, leaving
        // the DB without the link until a full reload.
        if (verifyRes.ok) {
          const linkedUsername =
            linkMethod === 'twitter'
              ? updatedUser.twitter?.username ?? null
              : updatedUser.farcaster?.username ?? null;
          if (linkedUsername) {
            recordedSocialsRef.current.add(`${linkMethod}:${linkedUsername}`);
          }
        }

        await refreshUserProfileCache(address);
      } catch (error) {
        console.error(`Failed to record ${linkMethod} verification on link:`, error);
      } finally {
        setIsLinkingAccount(false);
      }
    },
  });

  const { login } = useLogin({
    onComplete: ({ user }) => {
      let addr: string | null = null;
      for (const account of user.linkedAccounts as unknown as { type: string; walletClientType?: string; address?: string }[]) {
        if ((account.type === 'wallet' || account.type === 'smart_wallet') && account.address) {
          addr = account.address;
          break;
        }
      }
      if (addr) {
        const k = addr.toLowerCase();
        queryClient.prefetchQuery({
          queryKey: ['usdc-balance-cached', k],
          queryFn: () => fetchUsdcBalance(k),
        }).catch(() => {});
      }
    },
  });

  /** Blocks overlapping connect attempts — MetaMask throws -32002 if two `wallet_requestPermissions` calls overlap. */
  const connectBusyRef = useRef(false);
  const [connectBusy, setConnectBusy] = useState(false);

  // Drop per-viewer cached data when the user transitions out of an
  // authenticated state. We deliberately do NOT call `queryClient.clear()`
  // here even though the previous implementation did:
  //
  // `signInActiveWallet` (the wallet-switch path) does
  // `logout() → loginOrLink()`, so the user briefly transitions to
  // `authenticated = false` mid-flow. A blanket `clear()` at that point
  // wipes shared, viewer-independent caches too — chat-messages,
  // takeover-price, eth-price, lobby grid feed, board metadata — which
  // then ALL refetch when the new SIWE resolves. That fanout (plus
  // both SSE streams reconnecting) instantly trips the per-IP rate
  // limit in `middleware.ts`, cascading 429s across the UI and
  // surfacing the misleading "No messages yet" empty state in chat
  // while `chat-messages` is still mid-retry.
  //
  // `removeAddressScopedQueries` keeps the security guarantee
  // (no leaking the previous viewer's per-user data) without the
  // collateral damage. See `lib/query-cache-cleanup.ts` for the
  // address-segment heuristic.
  const wasAuthenticatedRef = useRef(false);
  useEffect(() => {
    if (authenticated) {
      wasAuthenticatedRef.current = true;
      return;
    }
    if (wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = false;
      removeAddressScopedQueries(queryClient);
    }
  }, [authenticated, queryClient]);

  // Find wallet objects from useWallets() for provider access (transactions, flaunch)
  const embeddedWallet = useMemo(() =>
    wallets.find(w => w.walletClientType === 'privy'), [wallets]);

  const wallet = useMemo(() => {
    if (!address) return embeddedWallet || null;
    const normalized = address.toLowerCase();
    return (
      wallets.find(w => w.address.toLowerCase() === normalized) ||
      embeddedWallet ||
      null
    );
  }, [address, wallets, embeddedWallet]);

  const hasExternalWallet = !!(wallet && wallet.walletClientType !== 'privy' && wallet.walletClientType !== 'privy-v2');
  const isEmbeddedWallet = !!(wallet && (wallet.walletClientType === 'privy' || wallet.walletClientType === 'privy-v2'));

  // Display name from linked accounts
  const displayName = useMemo(() => {
    if (!user) return null;
    const twitter = user.twitter;
    if (twitter?.username) return `@${twitter.username}`;
    const farcaster = user.farcaster;
    if (farcaster?.username) return farcaster.username;
    if (user.email?.address) return user.email.address.split('@')[0];
    return null;
  }, [user]);

  const avatarUrl = useMemo(() => {
    if (!user) return null;
    const farcaster = user.farcaster;
    if (farcaster?.pfp) return farcaster.pfp;
    const twitter = user.twitter;
    if (twitter?.profilePictureUrl) {
      return getHighResAvatarUrl(twitter.profilePictureUrl) ?? null;
    }
    return null;
  }, [user]);

  // Per-session dedupe set for `/api/verify-social` POSTs. We used to
  // dedupe via `useState` ("recordedTwitterUsername" / Farcaster), but
  // because Privy emits a fresh `user` object on every SDK tick the
  // owning effect re-fired constantly during wallet-switch / auth
  // churn, and any failed POST (e.g. the middleware-rate-limit 429s
  // we hit during the wallet-switch fanout) NEVER advanced the state
  // — so the next render re-checked, re-fetched, re-failed,
  // re-rendered, ad infinitum. The ref-based approach (declared near
  // the top of `useWallet`) decouples dedupe from React state
  // entirely. The key is added BEFORE the POST (so concurrent effect
  // runs can't double-fire) and removed again on failure: with the
  // effect's deps narrowed to the username strings a retry can only
  // happen on a real address/username transition or a remount, so
  // releasing the key is bounded and can't re-create the storm —
  // while keeping it would strand the link in Privy-only state for
  // the whole session (the "shows Connect X after linking" bug).
  const currentTwitterUsername = user?.twitter?.username ?? null;
  const currentFarcasterUsername = user?.farcaster?.username ?? null;

  useEffect(() => {
    if (!address) return;

    // Snapshot the ref locally so the closure can't see a later
    // mutation from a different effect tick.
    const recorded = recordedSocialsRef.current;

    const recordVerifications = async () => {
      const token = await getAccessToken();
      const authHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      // Shared shape for both platforms: claim the dedupe key up front
      // (concurrent runs skip), release it if the write didn't land
      // (`fetch` resolves on 401/429/503 — check `res.ok`, not just
      // the catch), and refresh the profile cache on success so the
      // Socials row / avatar flip without waiting out staleTime.
      const recordPlatform = (
        platform: 'twitter' | 'farcaster',
        username: string,
      ): Promise<void> | null => {
        const key = `${platform}:${username}`;
        if (recorded.has(key)) return null;
        recorded.add(key);
        return (async () => {
          try {
            const res = await fetch('/api/verify-social', {
              method: 'POST',
              headers: authHeaders,
              body: JSON.stringify({ platform }),
            });
            if (!res.ok) {
              recorded.delete(key);
              console.error(
                `Failed to record ${platform} verification: HTTP ${res.status}`,
              );
              return;
            }
            await refreshUserProfileCache(address);
          } catch (error) {
            recorded.delete(key);
            console.error(`Failed to record ${platform} verification:`, error);
          }
        })();
      };

      const tasks: Array<Promise<void>> = [];

      if (currentTwitterUsername) {
        const task = recordPlatform('twitter', currentTwitterUsername);
        if (task) tasks.push(task);
      }

      if (currentFarcasterUsername) {
        const task = recordPlatform('farcaster', currentFarcasterUsername);
        if (task) tasks.push(task);
      }

      await Promise.all(tasks);
    };

    recordVerifications();
    // Deps are deliberately the narrow username strings, NOT the
    // whole `user` object — Privy emits a fresh `user` reference on
    // every SDK tick which would re-fire this effect even when the
    // socials themselves haven't changed. `getAccessToken` is read
    // through a closure each call (it's stable enough at the Privy
    // SDK level for our purposes; if it ever wasn't, `await` would
    // surface the new token regardless).
  }, [
    address,
    currentTwitterUsername,
    currentFarcasterUsername,
    getAccessToken,
    refreshUserProfileCache,
  ]);

  // Track the address that the cache is currently committed to,
  // plus a pending timer. Privy's wallet-switch flow transiently
  // clears `address` to `null` before the new wallet surfaces, so
  // A→B briefly looks like A→null→B to this effect. Firing the
  // cascade on each step used to:
  //   1. reset A's observers (all address-scoped queries kicked into
  //      refetch the moment `address` flipped to null)
  //   2. then immediately reset them again for B a few frames later
  // …which doubled the refetch storm on every wallet change and
  // contributed to the staging 429 wall the user reported.
  //
  // We debounce the cascade instead: wait ~200ms for the address to
  // settle, then reset against the final value. If `address` lands
  // back on `previousAddress` inside the window the cascade is a
  // no-op (nothing actually changed), and a clean A→B only fires
  // the reset once against the final pair.
  const committedAddressRef = useRef<string | null>(null);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const nextAddress = address?.toLowerCase() || null;

    // Clear any in-flight debounce so rapid transitions collapse
    // to the latest value instead of stacking resets.
    if (pendingTimerRef.current !== null) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }

    if (nextAddress === committedAddressRef.current) {
      return;
    }

    pendingTimerRef.current = setTimeout(() => {
      pendingTimerRef.current = null;
      const committed = committedAddressRef.current;
      const finalAddress = address?.toLowerCase() || null;

      if (finalAddress === committed) {
        // Transient clear resolved back to the same address — nothing
        // to do (common on flaky wagmi reconnects).
        return;
      }

      if (committed && !finalAddress) {
        resetAddressKeyedQueries(queryClient, [committed]);
        removeAddressScopedQueries(queryClient);
      } else if (committed && finalAddress && committed !== finalAddress) {
        resetAddressKeyedQueries(queryClient, [committed, finalAddress]);
        resetQueriesByPrefixes(queryClient, ADDRESS_DEPENDENT_QUERY_PREFIXES);
      }

      committedAddressRef.current = finalAddress;
    }, WALLET_SWITCH_DEBOUNCE_MS);

    return () => {
      if (pendingTimerRef.current !== null) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, [address, queryClient]);

  const balanceAddressKey = address?.toLowerCase() ?? null;

  const { data: usdcBalance = 0n, isLoading: usdcLoading } = useQuery({
    queryKey: ['usdc-balance-cached', balanceAddressKey],
    queryFn: () => fetchUsdcBalance(balanceAddressKey!),
    enabled: !!balanceAddressKey,
    // Matched to `refetchInterval` so a second observer mounting
    // inside the staleness window (nav re-render, wallet dropdown
    // open) doesn't kick an extra refetch — the 45s interval is
    // the single source of truth for this key's poll cadence.
    staleTime: 45 * 1000,
    refetchInterval: 45 * 1000,
    // Inherit the global retry policy from `query-provider.tsx` —
    // it already short-circuits on `MiddlewareRateLimitError` (don't
    // retry our own per-IP cap, wait for the next refetchInterval)
    // and applies jittered exponential backoff for upstream rate
    // limits. The previous local `retry: 2` layered an extra two
    // attempts for non-rate-limit errors, contributing to the
    // /api/rpc/balance refetch storm during wallet-switch bursts.
    // Kept so the normal 45s poll doesn't flash the balance to skeleton.
    // On wallet switch, `resetQueries` above wipes the observer's prev
    // state so this placeholder has nothing to fall back to — the new
    // observer starts clean with `isLoading: true`, giving consumers a
    // real loading signal for skeleton rendering.
    placeholderData: (prev) => prev,
  });

  const { data: takeoverBalance = 0n, isLoading: takeoverLoading } = useQuery({
    queryKey: ['takeover-balance', balanceAddressKey],
    queryFn: () => fetchTakeoverBalance(balanceAddressKey!),
    enabled: !!balanceAddressKey,
    staleTime: 45 * 1000,
    refetchInterval: 45 * 1000,
    placeholderData: (prev) => prev,
  });

  // Single loading flag for wallet-scoped balance chrome (portfolio /
  // legacy wallet dropdowns). True while either balance fetch is in
  // flight for a connected wallet — consumers render a skeleton cell
  // instead of the previous wallet's numbers.
  const isBalancesLoading =
    !!balanceAddressKey && (usdcLoading || takeoverLoading);

  const refetchUsdcBalance = useCallback(() => {
    if (balanceAddressKey) {
      queryClient.fetchQuery({
        queryKey: ['usdc-balance-cached', balanceAddressKey],
        queryFn: () => fetchUsdcBalance(balanceAddressKey, true),
      }).catch(() => {});
    }
  }, [queryClient, balanceAddressKey]);

  const refetchTakeoverBalance = useCallback(() => {
    if (balanceAddressKey) {
      queryClient.fetchQuery({
        queryKey: ['takeover-balance', balanceAddressKey],
        queryFn: () => fetchTakeoverBalance(balanceAddressKey),
      }).catch(() => {});
    }
  }, [queryClient, balanceAddressKey]);

  const refreshAllBalances = useCallback(async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (balanceAddressKey) {
      await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['usdc-balance-cached', balanceAddressKey],
          queryFn: () => fetchUsdcBalance(balanceAddressKey, true),
        }).catch(() => {}),
        queryClient.fetchQuery({
          queryKey: ['takeover-balance', balanceAddressKey],
          queryFn: () => fetchTakeoverBalance(balanceAddressKey),
        }).catch(() => {}),
      ]);
    }
  }, [queryClient, balanceAddressKey]);

  const fundWallet = useCallback(async (options?: { amount?: string; asset?: 'USDC' | 'native-currency' }) => {
    if (!address) return;

    try {
      await privyFundWallet({
        address,
        options: {
          chain,
          amount: options?.amount || '10',
          asset: options?.asset || 'USDC',
          card: {
            preferredProvider: 'moonpay',
          },
        },
      });
      await refreshAllBalances();
    } catch (error) {
      console.error('Failed to fund wallet:', error);
      throw error;
    }
  }, [address, privyFundWallet, refreshAllBalances]);

  const points = 0;

  // Chain state from wagmi — no manual provider polling needed
  const currentChainId = wagmiChainId ?? null;
  const isCorrectNetwork = currentChainId === null ? true : currentChainId === expectedChainId;
  const expectedNetworkName = chain.name;
  const hasCheckedNetwork = currentChainId !== null;

  const switchNetwork = useCallback(async () => {
    try {
      await switchChainAsync({ chainId: expectedChainId });
    } catch (error) {
      console.error('Failed to switch network:', error);
      throw error;
    }
  }, [switchChainAsync, expectedChainId]);

  const handleLogout = useCallback(async () => {
    queryClient.clear();
    wagmiDisconnect();
    await privyLogout();
  }, [privyLogout, wagmiDisconnect, queryClient]);

  /**
   * On-demand "sign in as the wagmi-active wallet" callback.
   *
   * Behaviour at a glance:
   *   - Wagmi-active wallet already in the current Privy session →
   *     no-op, returns `{ ok: true, reason: 'already-linked' }`.
   *   - Otherwise → logout from the current Privy session (if any)
   *     and run `loginOrLink()` on the LOGIN path, which transparently
   *     handles wallets with or without an existing Privy user.
   *
   * One SIWE signature per click (or zero if already linked). See
   * {@link attemptSignInActiveWallet} for the full rationale, and
   * {@link attemptLinkActiveWallet} for the link-only primitive.
   *
   * Race notes (per Privy changelog + docs):
   *   - Always gate on `walletsReady` — the wallets array is fluid until then.
   *   - Always look up by address, never by index. Wallet ordering changes
   *     between renders ("Fixed wallet list ordering" entry in changelog).
   *   - Don't kick off `loginOrLink` from the same tick as a wagmi connect;
   *     callers should run this from an explicit user action / effect,
   *     not a connect callback.
   */
  const signInActiveWallet = useCallback(
    () =>
      attemptSignInActiveWallet({
        walletsReady,
        authenticated,
        wagmiAddress,
        linkedAddresses,
        wallets: wallets as unknown as ReadonlyArray<LinkableWallet>,
        // Privy logout only — must NOT touch wagmi (handleLogout is
        // the destructive variant). The next loginOrLink call needs
        // the wagmi connector intact so it can find the wallet entry
        // and ask its provider to sign.
        privyLogout,
      }),
    [walletsReady, authenticated, wagmiAddress, linkedAddresses, wallets, privyLogout],
  );

  // Logged-out users must go through `login()` so Privy runs the full auth modal
  // (wallet + social). `connectOrCreateWallet` targets embedded/external wallet
  // flows and can race MetaMask with other hooks — use the standard login path.
  const connectWallet = useCallback(
    (e?: MouseEvent) => {
      if (connectBusyRef.current || !ready) return;
      connectBusyRef.current = true;
      setConnectBusy(true);
      window.setTimeout(() => {
        connectBusyRef.current = false;
        setConnectBusy(false);
      }, 2500);
      if (!authenticated) {
        login(e);
        return;
      }
      linkWallet(e);
    },
    [authenticated, linkWallet, login, ready],
  );

  if (tutorialWallet) return tutorialWallet;

  return {
    ready,
    authenticated,
    isConnected: authenticated && !!address,
    /**
     * True when `address` is a wallet Privy has linked to the current
     * session — i.e. the backend's `requireAuth(request, address)` will
     * accept requests naming this address. Consumers that hit auth-gated
     * endpoints (`/api/notifications`, push subscribe, etc.) should add
     * this to their `enabled` guard so an unlinked in-browser wallet
     * (e.g. MetaMask mid-switch) doesn't spam 403s.
     */
    isAddressLinked,
    hasExternalWallet,
    isEmbeddedWallet,
    isLoading: false,
    /** True briefly after opening connect / link — disables double-clicks that trigger MetaMask -32002. */
    connectBusy,

    address,
    /**
     * Every wallet linked to the current Privy session, lowercased.
     * Already exists internally as the source for `isAddressLinked`;
     * surfaced here so consumers like `useGridPage` can match a
     * reservation `claimant` against the FULL set of viewer wallets,
     * not just the wagmi-active one. The user reproed on grid
     * `0x5089…-17` is exactly this case: the V1 holder EOA is the
     * reservation claimant, but Privy's Twitter login surfaces an
     * embedded wallet as the wagmi-active address — without this set
     * the lobby's "your reservation" rim never paints.
     */
    linkedAddresses,
    embeddedAddress: embeddedWallet?.address || null,
    /** True once Privy has finished hydrating its connected-wallets
     *  list. Effects that call `signInActiveWallet()` must wait for
     *  this so the wagmi-active wallet has settled into
     *  `useWallets()` (per Privy docs Get connected wallets →
     *  Waiting for wallets to be ready). */
    walletsReady,
    /**
     * On-demand "sign in as the wagmi-active wallet" primitive. Used
     * by chat: if the active wallet is already linked, this is a
     * no-op; otherwise it logs out from the current Privy session
     * (if any) and runs `loginOrLink` on the LOGIN path, which
     * transparently handles wallets that already own their own Privy
     * user. See {@link attemptSignInActiveWallet} for the rationale.
     */
    signInActiveWallet,

    displayName,
    avatarUrl,
    usdcBalance,
    refetchUsdcBalance,
    takeoverBalance,
    refetchTakeoverBalance,
    /** True while either wallet-scoped balance query (`usdc-balance-cached`, `takeover-balance`) is loading — drives balance row skeletons in the portfolio / legacy wallet dropdowns on wallet switch. */
    isBalancesLoading,
    refreshAllBalances,
    points,
    connectWallet,
    logout: handleLogout,
    fundWallet,
    exportWallet,
    getAccessToken,
    linkTwitter,
    linkFarcaster,
    isLinkingAccount,
    wallet,
    user,

    currentChainId,
    expectedChainId,
    expectedNetworkName,
    isCorrectNetwork,
    hasCheckedNetwork,
    switchNetwork,
  };
}
